import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, Search, Filter, X, ChevronDown, ChevronUp, Loader2, Eye, Plus, Flag } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'react-hot-toast';
import { useRbac } from '../../context';
import { getAllCandidates } from '../../services/candidateapi';
import { getCandidateRecords, getFilterValues, searchFilterValues, incrementPhoneViewCount, toggleCandidateFlag, CandidateRecord } from '../../services/candidateRecordsApi';
import { IndianRupee } from "lucide-react";
import { Briefcase } from "lucide-react";
import AddApproachModal from '../../components/AddApproachModal';

// Use the new CandidateRecord interface from the API service
type Candidate = CandidateRecord;

// SearchBox - completely isolated with memoization to prevent parent re-renders
const SearchBox = React.memo(({ 
  onSearchChange, 
  onSearchClick, 
  searchTerm,
  loading 
}: { 
  onSearchChange: (value: string) => void;
  onSearchClick: () => void;
  searchTerm: string;
  loading?: boolean;
}) => {
  const [localValue, setLocalValue] = useState(searchTerm);

  // Sync local value with parent searchTerm when it changes externally
  useEffect(() => {
    setLocalValue(searchTerm);
  }, [searchTerm]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalValue(value); // Instant UI update
    onSearchChange(value); // Update parent searchTerm (not triggering API)
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearchClick();
    }
  };

  return (
    <div className="mt-2 sm:mt-3 w-full">
      <div className="flex gap-2 w-full">
        <div className="relative flex-1">
          <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3 sm:h-4 sm:w-4 pointer-events-none" />
          <Input
            placeholder="Search candidates (min. 3 chars)..."
            value={localValue}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            disabled={loading}
            className="pl-8 sm:pl-10 w-full h-8 sm:h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:opacity-60"
          />
        </div>
        <Button
          onClick={onSearchClick}
          disabled={loading || localValue.trim().length < 3}
          className="h-8 sm:h-10 w-8 sm:w-10 p-0 bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          size="icon"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
});

// Reusable searchable select component - memoized with proper comparison
const SearchableSelect = React.memo(({ 
  label, 
  value, 
  onValueChange, 
  options, 
  searchValue, 
  onSearchChange, 
  placeholder 
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: string[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  placeholder: string;
}) => (
  <div>
    <Label className="text-sm font-medium text-gray-700">{label}</Label>
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="w-full h-10 mt-1">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-60">
        <div className="p-2 border-b">
          <Input
            placeholder={`Search ${placeholder.toLowerCase()}...`}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="max-h-48 overflow-y-auto">
          <SelectItem value="all">All {placeholder}</SelectItem>
          {options.map(option => (
            <SelectItem key={option} value={option}>{option}</SelectItem>
          ))}
          {options.length === 0 && searchValue && (
            <div className="p-2 text-sm text-gray-500 text-center">
              No {placeholder.toLowerCase()} found
            </div>
          )}
        </div>
      </SelectContent>
    </Select>
  </div>
), (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.value === nextProps.value &&
    prevProps.searchValue === nextProps.searchValue &&
    prevProps.options.length === nextProps.options.length &&
    prevProps.options.join(',') === nextProps.options.join(',')
  );
});

 SearchableSelect.displayName = 'SearchableSelect';

// Utility function to highlight matching text
const highlightMatch = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || searchTerm.length < 3 || !text) {
    return text;
  }
  
  const searchLower = searchTerm.toLowerCase();
  const textLower = text.toLowerCase();
  const index = textLower.indexOf(searchLower);
  
  if (index === -1) {
    return text;
  }
  
  const beforeMatch = text.substring(0, index);
  const match = text.substring(index, index + searchTerm.length);
  const afterMatch = text.substring(index + searchTerm.length);
  
  return (
    <>
      {beforeMatch}
      <span className="bg-yellow-200 text-gray-900 px-0.5 rounded font-medium">{match}</span>
      {afterMatch}
    </>
  );
};

// Recursive function for highlighting multiple matches in text
const highlightAllMatches = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || searchTerm.length < 3 || !text) {
    return text;
  }
  
  const parts = [];
  const searchLower = searchTerm.toLowerCase();
  const textLower = text.toLowerCase();
  let lastIndex = 0;
  
  while (true) {
    const index = textLower.indexOf(searchLower, lastIndex);
    if (index === -1) break;
    
    if (index > lastIndex) {
      parts.push(text.substring(lastIndex, index));
    }
    
    parts.push(
      <span key={index} className="bg-yellow-200 text-gray-900 px-0.5 rounded font-medium">
        {text.substring(index, index + searchTerm.length)}
      </span>
    );
    
    lastIndex = index + searchTerm.length;
  }
  
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return <>{parts.length > 0 ? parts : text}</>;
};
  
  const CandidateDatabase: React.FC = () => {
  // Add CSS animation keyframes
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInFromRight {
        0% {
          transform: translateX(100%);
        }
        100% {
          transform: translateX(0);
        }
      }
      
      /* Mobile dropdown optimizations */
      @media (max-width: 640px) {
        [data-radix-popper-content-wrapper] {
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          z-index: 9999 !important;
        }
        
        [data-radix-select-content] {
          max-height: 70vh !important;
          width: 90vw !important;
          max-width: 400px !important;
        }
        
        /* Prevent body scroll when dropdown is open */
        body:has([data-radix-select-content]) {
          overflow: hidden !important;
        }
        
        /* Ensure dropdowns don't get cut off */
        [data-radix-select-content] {
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          border: 1px solid #e5e7eb !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [flagConfirmCandidateId, setFlagConfirmCandidateId] = useState<string | null>(null);
  const [showApproachModal, setShowApproachModal] = useState(false);
  const [selectedCandidateForApproach, setSelectedCandidateForApproach] = useState<{
    id: string;
    name: string;
    email: string;
    phone: string;
    highestQualification?: string;
    profileSource?: string;
    dob?: string;
    dateOfBirth?: string;
    skills?: string[];
    workExperience?: number;
  } | null>(null);
  useEffect(() => {
    console.log("selectedCandidateForApproach:<==", selectedCandidateForApproach)
  }, [selectedCandidateForApproach])
  // Debounced search term for API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // State for filter search
  const [citySearch, setCitySearch] = useState('');
  const [functionalAreaSearch, setFunctionalAreaSearch] = useState('');
  const [industrySearch, setIndustrySearch] = useState('');
  const [levelSearch, setLevelSearch] = useState('');
  const [skillsSearch, setSkillsSearch] = useState('');
  // Filter states - using arrays for multi-select checkbox filters
  const [selectedGender, setSelectedGender] = useState<string[]>([]);
  const [selectedCity, setSelectedCity] = useState<string[]>([]);
  const [selectedFunctionalArea, setSelectedFunctionalArea] = useState<string[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  // Salary range state
  const [minSalary, setMinSalary] = useState<number | undefined>(undefined);
  const [maxSalary, setMaxSalary] = useState<number | undefined>(undefined);
  // Work experience range state
  const [minExperience, setMinExperience] = useState<number | undefined>(undefined);
  const [maxExperience, setMaxExperience] = useState<number | undefined>(undefined);
  // Age range state (for date of birth filtering)
  const [minAge, setMinAge] = useState<number | undefined>(undefined);
  const [maxAge, setMaxAge] = useState<number | undefined>(undefined);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const ITEMS_PER_PAGE = 15;
  
  // Filter panel state
  const [showFilters, setShowFilters] = useState(false);
  
  // Responsive filter state
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  
  // Filter expand/collapse state - all collapsed by default
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(
    new Set([])
  );
  
  const isMountedRef = useRef(true);
  const hasInitialFetchRef = useRef(false);
  const apiCallInProgressRef = useRef(false);
  const prevPageRef = useRef(1);
  const filterDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevFiltersRef = useRef({
    debouncedSearchTerm: '',
    selectedGender: [] as string[],
    selectedCity: [] as string[],
    selectedFunctionalArea: [] as string[],
    selectedIndustry: [] as string[],
    selectedLevel: [] as string[],
    selectedSkills: [] as string[],
    minSalary: undefined as number | undefined,
    maxSalary: undefined as number | undefined,
    minExperience: undefined as number | undefined,
    maxExperience: undefined as number | undefined,
    minAge: undefined as number | undefined,
    maxAge: undefined as number | undefined
  });

  
  // State for showing phone number
  const [showPhoneNumbers, setShowPhoneNumbers] = useState<{ [key: string]: boolean }>({});
  
  // State for expanded sections per candidate
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: Set<string> }>({});
  
  // Refs for search inputs to maintain focus
  const citySearchRef = useRef<HTMLInputElement>(null);
  const functionalAreaSearchRef = useRef<HTMLInputElement>(null);
  const industrySearchRef = useRef<HTMLInputElement>(null);
  const levelSearchRef = useRef<HTMLInputElement>(null);
  const skillsSearchRef = useRef<HTMLInputElement>(null);

  // Calculate age from DOB string (YYYY-MM-DD format)
  const calculateAge = useCallback((dobString: string): number | null => {
    if (!dobString) return null;
    
    try {
      // Parse DOB string in YYYY-MM-DD format
      const dob = new Date(dobString);
      if (isNaN(dob.getTime())) return null;
      
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      
      // Adjust age if birthday hasn't occurred this year
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      
      return age;
    } catch (error) {
      console.error('Error calculating age:', error);
      return null;
    }
  }, []);

  // Copy contact to clipboard
  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Contact copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy contact');
    }
  }, []);

  // State to track phone view counts (for display updates)
  const [phoneViewCounts, setPhoneViewCounts] = useState<{ [key: string]: number }>({});

  // Toggle phone number visibility and increment view count
  const togglePhoneNumber = useCallback(async (candidateId: string) => {
    const isCurrentlyVisible = showPhoneNumbers[candidateId];
    
    // If showing the phone for the first time, increment the count
    if (!isCurrentlyVisible) {
      try {
        const newCount = await incrementPhoneViewCount(candidateId);
        setPhoneViewCounts(prev => ({
          ...prev,
          [candidateId]: newCount
        }));
      } catch (error) {
        console.error('Error incrementing phone view count:', error);
        // Still show the phone even if API call fails
      }
    }
    
    setShowPhoneNumbers(prev => ({
      ...prev,
      [candidateId]: !prev[candidateId]
    }));
  }, [showPhoneNumbers]);

  // Toggle section expansion
  const toggleSection = useCallback((candidateId: string, sectionName: string) => {
    setExpandedSections(prev => {
      const candidateSections = prev[candidateId] || new Set();
      const newSections = new Set(candidateSections);
      
      if (newSections.has(sectionName)) {
        newSections.delete(sectionName);
      } else {
        newSections.add(sectionName);
      }
      
      return {
        ...prev,
        [candidateId]: newSections
      };
    });
  }, []);

  // Check if section is expanded
  const isSectionExpanded = (candidateId: string, sectionName: string) => {
    return expandedSections[candidateId]?.has(sectionName) || false;
  };

  // Load candidates from API on component mount only
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Only load on initial mount
        if (!hasInitialFetchRef.current) {
          setCandidates([]);
          setCurrentPage(1);
          
          // Fetch candidates data
          await fetchCandidates(1);
          hasInitialFetchRef.current = true;
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        toast.error('Failed to load candidate data');
      }
    };
    
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCandidates = useCallback(async (page: number, retryCount = 0) => {
    // Prevent multiple simultaneous API calls using ref
    if (apiCallInProgressRef.current) {
      console.log('API call already in progress, skipping...');
      return;
    }

    try {
      apiCallInProgressRef.current = true;
      setLoading(true);
      
      // Build API parameters for candidateRecords
      const apiParams: any = {
        page,
        limit: ITEMS_PER_PAGE
      };

      // Add search term (use debounced version) - only if 3+ characters
      if (debouncedSearchTerm.trim().length >= 3) {
        apiParams.search = debouncedSearchTerm.trim();
      }

      // Add filters (arrays for multi-select)
      if (selectedGender.length > 0) {
        apiParams.gender = selectedGender;
      }
      if (selectedCity.length > 0) {
        apiParams.city = selectedCity;
      }
      if (selectedFunctionalArea.length > 0) {
        apiParams.functionalArea = selectedFunctionalArea;
      }
      if (selectedIndustry.length > 0) {
        apiParams.industry = selectedIndustry;
      }
      if (selectedLevel.length > 0) {
        apiParams.level = selectedLevel;
      }
      if (selectedSkills.length > 0) {
        apiParams.skills = selectedSkills;
      }
      if (minSalary !== undefined) {
        apiParams.minSalary = minSalary;
      }
      if (maxSalary !== undefined) {
        apiParams.maxSalary = maxSalary;
      }
      if (minExperience !== undefined) {
        apiParams.minExperience = minExperience;
      }
    if (maxExperience !== undefined) {
      apiParams.maxExperience = maxExperience;
    }
    if (minAge !== undefined) {
      apiParams.minAge = minAge;
    }
    if (maxAge !== undefined) {
      apiParams.maxAge = maxAge;
      }

      console.log('Fetching candidate records with params:', apiParams);
      
      const response = await getCandidateRecords(apiParams);
      
      // Only update state if component is still mounted
      if (!isMountedRef.current) {
        apiCallInProgressRef.current = false;
        return;
      }

      setCandidates(response.data.candidates);
      setTotalResults(response.data.totalCount);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page); // Set current page after data is loaded
      
      // Initialize phone view counts from candidate data
      const countsMap: { [key: string]: number } = {};
      response.data.candidates.forEach((candidate: Candidate) => {
        if (candidate._id && candidate.phoneViewCount !== undefined) {
          countsMap[candidate._id] = candidate.phoneViewCount;
        }
      });
      setPhoneViewCounts(prev => ({ ...prev, ...countsMap }));
      
      // Clear loading after updating state
      setLoading(false);
      
      // Scroll to top after data loads
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 50);
      
    } catch (error) {
      console.error('Error fetching candidate records:', error);
      setLoading(false);
      
      // Retry logic for network errors
      if (retryCount < 2 && (error as any)?.code === 'NETWORK_ERROR') {
        console.log(`Retrying fetchCandidates (attempt ${retryCount + 1})...`);
        setTimeout(() => {
          fetchCandidates(page, retryCount + 1);
        }, 1000 * (retryCount + 1)); // Exponential backoff
        return;
      }
      
      toast.error('Failed to fetch candidate records');
    } finally {
      apiCallInProgressRef.current = false;
    }
  }, [debouncedSearchTerm, selectedGender, selectedCity, selectedFunctionalArea, selectedIndustry, selectedLevel, selectedSkills, minSalary, maxSalary, minExperience, maxExperience, minAge, maxAge]);

  // Handle page changes
  const handlePageChange = useCallback((page: number) => {
    console.log('handlePageChange called with:', { page, currentPage, totalPages });
    
    // Detailed condition checking
    if (page < 1) {
      console.log('Page change blocked: page < 1');
      return;
    }
    if (page > totalPages) {
      console.log('Page change blocked: page > totalPages');
      return;
    }
    if (page === currentPage) {
      console.log('Page change blocked: page === currentPage (already on this page)');
      return;
    }
    
    // All conditions passed, proceed with page change
    console.log('Fetching candidates for page:', page);
    window.scrollTo({ top: 0, behavior: 'auto' });
    fetchCandidates(page); // fetchCandidates will set currentPage after data loads
  }, [totalPages, currentPage, fetchCandidates]);

  // Scroll to top when page changes - removed candidates dependency to prevent excessive re-renders
  useEffect(() => {
    // Only scroll if page actually changed
    if (prevPageRef.current !== currentPage && currentPage > 1) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      prevPageRef.current = currentPage;
    }
  }, [currentPage]);

  // Refetch when filters change and reset to page 1
  useEffect(() => {
    // Skip if this is the initial load (handled by another useEffect)
    if (!hasInitialFetchRef.current) {
      return;
    }

    // Skip if API call is already in progress
    if (apiCallInProgressRef.current) {
      return;
    }

    // Check if filters actually changed
    const currentFilters = {
      debouncedSearchTerm,
      selectedGender,
      selectedCity,
      selectedFunctionalArea,
      selectedIndustry,
      selectedLevel,
      selectedSkills,
      minSalary,
      maxSalary,
      minExperience,
      maxExperience,
      minAge,
      maxAge
    };

    const filtersChanged = 
      // debouncedSearchTerm changes only when search button is clicked (manual trigger)
      currentFilters.debouncedSearchTerm !== prevFiltersRef.current.debouncedSearchTerm ||
      JSON.stringify(currentFilters.selectedGender) !== JSON.stringify(prevFiltersRef.current.selectedGender) ||
      JSON.stringify(currentFilters.selectedCity) !== JSON.stringify(prevFiltersRef.current.selectedCity) ||
      JSON.stringify(currentFilters.selectedFunctionalArea) !== JSON.stringify(prevFiltersRef.current.selectedFunctionalArea) ||
      JSON.stringify(currentFilters.selectedIndustry) !== JSON.stringify(prevFiltersRef.current.selectedIndustry) ||
      JSON.stringify(currentFilters.selectedLevel) !== JSON.stringify(prevFiltersRef.current.selectedLevel) ||
      JSON.stringify(currentFilters.selectedSkills) !== JSON.stringify(prevFiltersRef.current.selectedSkills) ||
      currentFilters.minSalary !== prevFiltersRef.current.minSalary ||
      currentFilters.maxSalary !== prevFiltersRef.current.maxSalary ||
      currentFilters.minExperience !== prevFiltersRef.current.minExperience ||
      currentFilters.maxExperience !== prevFiltersRef.current.maxExperience ||
      currentFilters.minAge !== prevFiltersRef.current.minAge ||
      currentFilters.maxAge !== prevFiltersRef.current.maxAge;

    if (!filtersChanged) {
      return;
    }

    // Only auto-trigger filter for checkbox filters, not for range inputs
    // Range inputs (salary, experience, age) require manual submission via Apply Filters button
    // Exception: When clearing range filters (setting to undefined), auto-trigger the API call
    
    // Check if only range filters changed (these require manual submission)
    const onlyRangeFiltersChanged = 
      currentFilters.minSalary !== prevFiltersRef.current.minSalary ||
      currentFilters.maxSalary !== prevFiltersRef.current.maxSalary ||
      currentFilters.minExperience !== prevFiltersRef.current.minExperience ||
      currentFilters.maxExperience !== prevFiltersRef.current.maxExperience ||
      currentFilters.minAge !== prevFiltersRef.current.minAge ||
      currentFilters.maxAge !== prevFiltersRef.current.maxAge;
      
    const checkboxFiltersChanged =
      // Note: debouncedSearchTerm is NOT included here - search is now manual via button
      JSON.stringify(currentFilters.selectedGender) !== JSON.stringify(prevFiltersRef.current.selectedGender) ||
      JSON.stringify(currentFilters.selectedCity) !== JSON.stringify(prevFiltersRef.current.selectedCity) ||
      JSON.stringify(currentFilters.selectedFunctionalArea) !== JSON.stringify(prevFiltersRef.current.selectedFunctionalArea) ||
      JSON.stringify(currentFilters.selectedIndustry) !== JSON.stringify(prevFiltersRef.current.selectedIndustry) ||
      JSON.stringify(currentFilters.selectedLevel) !== JSON.stringify(prevFiltersRef.current.selectedLevel) ||
      JSON.stringify(currentFilters.selectedSkills) !== JSON.stringify(prevFiltersRef.current.selectedSkills);
    
    // Check if range filters are being cleared (going from a value to undefined)
    const rangeFiltersBeingCleared = 
      (prevFiltersRef.current.minSalary !== undefined && currentFilters.minSalary === undefined) ||
      (prevFiltersRef.current.maxSalary !== undefined && currentFilters.maxSalary === undefined) ||
      (prevFiltersRef.current.minExperience !== undefined && currentFilters.minExperience === undefined) ||
      (prevFiltersRef.current.maxExperience !== undefined && currentFilters.maxExperience === undefined) ||
      (prevFiltersRef.current.minAge !== undefined && currentFilters.minAge === undefined) ||
      (prevFiltersRef.current.maxAge !== undefined && currentFilters.maxAge === undefined);
    
    // If only range filters changed, don't auto-trigger - user must click Apply Filters
    // Exception: If clearing range filters, auto-trigger the API call
    if (onlyRangeFiltersChanged && !checkboxFiltersChanged && !rangeFiltersBeingCleared) {
      prevFiltersRef.current = currentFilters;
      return;
    }

    // Update previous filters
    prevFiltersRef.current = currentFilters;

    // Clear any existing timeout
    if (filterDebounceTimeoutRef.current) {
      clearTimeout(filterDebounceTimeoutRef.current);
    }

    // Debounce filter changes by 900ms (applies to checkboxes only)
    filterDebounceTimeoutRef.current = setTimeout(() => {
      const executeSearch = async () => {
      apiCallInProgressRef.current = true;
      setLoading(true);
      
      try {
        // Build API parameters for candidateRecords
        const apiParams: any = {
          page: 1,
          limit: ITEMS_PER_PAGE
        };

        // Add search term (use debounced version) - only if 3+ characters
        if (debouncedSearchTerm.trim().length >= 3) {
          apiParams.search = debouncedSearchTerm.trim();
        }

        // Add filters (arrays for multi-select)
        if (selectedGender.length > 0) {
          apiParams.gender = selectedGender;
        }
        if (selectedCity.length > 0) {
          apiParams.city = selectedCity;
        }
        if (selectedFunctionalArea.length > 0) {
          apiParams.functionalArea = selectedFunctionalArea;
        }
        if (selectedIndustry.length > 0) {
          apiParams.industry = selectedIndustry;
        }
        if (selectedLevel.length > 0) {
          apiParams.level = selectedLevel;
        }
          if (selectedSkills.length > 0) {
            apiParams.skills = selectedSkills;
          }
          if (minSalary !== undefined) {
            apiParams.minSalary = minSalary;
          }
          if (maxSalary !== undefined) {
            apiParams.maxSalary = maxSalary;
          }
          if (minExperience !== undefined) {
            apiParams.minExperience = minExperience;
          }
          if (maxExperience !== undefined) {
            apiParams.maxExperience = maxExperience;
          }
          if (minAge !== undefined) {
            apiParams.minAge = minAge;
          }
          if (maxAge !== undefined) {
            apiParams.maxAge = maxAge;
          }

        console.log('Fetching candidate records with params (filter change):', apiParams);
        
        const response = await getCandidateRecords(apiParams);
        
        // Only update state if component is still mounted
        if (!isMountedRef.current) {
          apiCallInProgressRef.current = false;
          return;
        }

        setCandidates(response.data.candidates);
        setTotalResults(response.data.totalCount);
        setTotalPages(response.data.totalPages);
        setCurrentPage(1);
          
          // Initialize phone view counts from candidate data
          const countsMap: { [key: string]: number } = {};
          response.data.candidates.forEach((candidate: Candidate) => {
            if (candidate._id && candidate.phoneViewCount !== undefined) {
              countsMap[candidate._id] = candidate.phoneViewCount;
            }
          });
          setPhoneViewCounts(prev => ({ ...prev, ...countsMap }));
        
        // Clear loading after updating state
        setLoading(false);
        
        // Scroll to top after data loads
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'auto' });
        }, 50);
      } catch (error) {
        console.error('Error fetching candidate records:', error);
        setLoading(false);
        toast.error('Failed to fetch candidate records');
      } finally {
        apiCallInProgressRef.current = false;
      }
      };

      executeSearch();
    }, 900);

    // Cleanup timeout on unmount or when filters change again
    return () => {
      if (filterDebounceTimeoutRef.current) {
        clearTimeout(filterDebounceTimeoutRef.current);
      }
    };
  }, [debouncedSearchTerm, selectedGender, selectedCity, selectedFunctionalArea, selectedIndustry, selectedLevel, selectedSkills, minSalary, maxSalary, minExperience, maxExperience, minAge, maxAge]);

  // Filter candidates (now handled by API, but keeping for client-side additional filtering if needed)
  const filteredCandidates = useMemo(() => {
    // Return candidates directly since filtering is handled by API
    // This prevents unnecessary array operations
    return candidates;
  }, [candidates]);

  // Memoized button handlers to prevent recreation on every render
  const handleNextPage = useCallback(() => {
    handlePageChange(currentPage + 1);
  }, [currentPage, handlePageChange]);

  const handlePrevPage = useCallback(() => {
    handlePageChange(currentPage - 1);
  }, [currentPage, handlePageChange]);

  const handlePageClick = useCallback((page: number) => {
    handlePageChange(page);
  }, [handlePageChange]);

  // Memoize page buttons to prevent re-computing on every render
  const pageButtons = useMemo(() => {
    const pages = [];
    const maxVisible = 7; // Show max 7 page numbers
    let startPage = 1;
    let endPage = totalPages;
    
    // Calculate visible page range
    if (totalPages > maxVisible) {
      const half = Math.floor(maxVisible / 2);
      startPage = Math.max(1, currentPage - half);
      endPage = Math.min(totalPages, startPage + maxVisible - 1);
      
      if (endPage - startPage < maxVisible - 1) {
        startPage = Math.max(1, endPage - maxVisible + 1);
      }
    }
    
    // First page and ellipsis
    if (startPage > 1) {
      pages.push(
        <Button
          key="1"
          variant={currentPage === 1 ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageClick(1)}
          disabled={loading}
          className={currentPage === 1 ? "bg-orange-600 hover:bg-orange-700" : "border-orange-300 text-orange-600 hover:bg-orange-50"}
        >
          1
        </Button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="px-2 text-gray-400">...</span>);
      }
    }
    
    // Middle page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageClick(i)}
          disabled={loading}
          className={currentPage === i ? "bg-orange-600 hover:bg-orange-700" : "border-orange-300 text-orange-600 hover:bg-orange-50"}
        >
          {i}
        </Button>
      );
    }
    
    // Remove last page button - user requested to not show it
    
    return pages;
  }, [currentPage, totalPages, loading, handlePageClick]);



  // Toggle filter expand/collapse
  const toggleFilterExpansion = useCallback((filterName: string) => {
    setExpandedFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filterName)) {
        newSet.delete(filterName);
      } else {
        newSet.add(filterName);
      }
      return newSet;
    });
  }, []);

  // Trigger manual filter application - direct API call
  const applyFilters = useCallback(async () => {
    if (loading || apiCallInProgressRef.current) {
      return;
    }
    
    // Clear any pending filter debounce
    if (filterDebounceTimeoutRef.current) {
      clearTimeout(filterDebounceTimeoutRef.current);
      filterDebounceTimeoutRef.current = null;
    }
    
    // Build API parameters
    const apiParams: any = {
      page: 1,
      limit: ITEMS_PER_PAGE
    };

    // Add search term - only if 3+ characters
    if (debouncedSearchTerm.trim().length >= 3) {
      apiParams.search = debouncedSearchTerm.trim();
    }

    // Add filters (arrays for multi-select)
    if (selectedGender.length > 0) apiParams.gender = selectedGender;
    if (selectedCity.length > 0) apiParams.city = selectedCity;
    if (selectedFunctionalArea.length > 0) apiParams.functionalArea = selectedFunctionalArea;
    if (selectedIndustry.length > 0) apiParams.industry = selectedIndustry;
    if (selectedLevel.length > 0) apiParams.level = selectedLevel;
    if (selectedSkills.length > 0) apiParams.skills = selectedSkills;
    if (minSalary !== undefined) apiParams.minSalary = minSalary;
    if (maxSalary !== undefined) apiParams.maxSalary = maxSalary;
    if (minExperience !== undefined) apiParams.minExperience = minExperience;
    if (maxExperience !== undefined) apiParams.maxExperience = maxExperience;
    if (minAge !== undefined) apiParams.minAge = minAge;
    if (maxAge !== undefined) apiParams.maxAge = maxAge;

    // Call API directly
    apiCallInProgressRef.current = true;
    setLoading(true);
    
    try {
      console.log('Applying filters:', apiParams);
      const response = await getCandidateRecords(apiParams);
      
      if (!isMountedRef.current) {
        apiCallInProgressRef.current = false;
        return;
      }

      setCandidates(response.data.candidates);
      setTotalResults(response.data.totalCount);
      setTotalPages(response.data.totalPages);
      setCurrentPage(1);
      
      // Initialize phone view counts
      const countsMap: { [key: string]: number } = {};
      response.data.candidates.forEach((candidate: Candidate) => {
        if (candidate._id && candidate.phoneViewCount !== undefined) {
          countsMap[candidate._id] = candidate.phoneViewCount;
        }
      });
      setPhoneViewCounts(prev => ({ ...prev, ...countsMap }));
      
      setLoading(false);
      
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 50);
    } catch (error) {
      console.error('Error applying filters:', error);
      setLoading(false);
      toast.error('Failed to apply filters');
    } finally {
      apiCallInProgressRef.current = false;
    }
  }, [loading, debouncedSearchTerm, selectedGender, selectedCity, selectedFunctionalArea, selectedIndustry, selectedLevel, selectedSkills, minSalary, maxSalary, minExperience, maxExperience, minAge, maxAge]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedGender([]);
    setSelectedCity([]);
    setSelectedFunctionalArea([]);
    setSelectedIndustry([]);
    setSelectedLevel([]);
    setSelectedSkills([]);
    setMinSalary(undefined);
    setMaxSalary(undefined);
    setMinExperience(undefined);
    setMaxExperience(undefined);
    setMinAge(undefined);
    setMaxAge(undefined);
    
    // Clear search states
    setCitySearch('');
    setFunctionalAreaSearch('');
    setIndustrySearch('');
    setLevelSearch('');
    setSkillsSearch('');
    
    // Reset to page 1
    setCurrentPage(1);
    fetchCandidates(1);
  }, [fetchCandidates]);

  const activeFilters = useMemo(() => {
    const filters = [];
    if (selectedCity.length > 0) filters.push({ key: 'city', values: selectedCity, label: 'City' });
    if (selectedFunctionalArea.length > 0) filters.push({ key: 'functionalArea', values: selectedFunctionalArea, label: 'Functional Area' });
    if (selectedIndustry.length > 0) filters.push({ key: 'industry', values: selectedIndustry, label: 'Industry' });
    if (selectedLevel.length > 0) filters.push({ key: 'level', values: selectedLevel, label: 'Level' });
    if (selectedSkills.length > 0) filters.push({ key: 'skills', values: selectedSkills, label: 'Skills' });
    if (minSalary !== undefined || maxSalary !== undefined) {
      const min = minSalary !== undefined ? minSalary.toLocaleString('en-IN') : '0';
      const max = maxSalary !== undefined ? maxSalary.toLocaleString('en-IN') : '∞';
      filters.push({ key: 'salary', values: [`₹${min} - ₹${max}`], label: 'Salary' });
    }
    if (minExperience !== undefined || maxExperience !== undefined) {
      const min = minExperience !== undefined ? minExperience : '0';
      const max = maxExperience !== undefined ? maxExperience : '∞';
      filters.push({ key: 'experience', values: [`${min} - ${max} yrs`], label: 'Experience' });
    }
    if (minAge !== undefined || maxAge !== undefined) {
      const min = minAge !== undefined ? minAge : '0';
      const max = maxAge !== undefined ? maxAge : '∞';
      filters.push({ key: 'age', values: [`${min} - ${max} yrs`], label: 'Age' });
    }
    return filters;
  }, [selectedGender, selectedCity, selectedFunctionalArea, selectedIndustry, selectedLevel, selectedSkills, minSalary, maxSalary, minExperience, maxExperience, minAge, maxAge]);

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'active':
        return { label: 'Active', className: 'bg-green-100 text-green-800' };
      case 'inactive':
        return { label: 'Inactive', className: 'bg-red-100 text-red-800' };
      case 'pending':
        return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' };
      default:
        return { label: status, className: 'bg-gray-100 text-gray-800' };
    }
  };

  // Get unique values for filter options from API
  const [filterValues, setFilterValues] = useState({
    cities: [] as string[],
    functionalAreas: [] as string[],
    industries: [] as string[],
    levels: [] as string[],
    genders: [] as string[],
    skills: [] as string[]
  });
  
  // State for API-searched filter results
  const [searchedCityValues, setSearchedCityValues] = useState<string[]>([]);
  const [searchedFunctionalAreaValues, setSearchedFunctionalAreaValues] = useState<string[]>([]);
  const [searchedIndustryValues, setSearchedIndustryValues] = useState<string[]>([]);
  const [searchedLevelValues, setSearchedLevelValues] = useState<string[]>([]);
  
  // Loading states for filter searches
  const [isSearchingCities, setIsSearchingCities] = useState(false);
  const [isSearchingFunctionalAreas, setIsSearchingFunctionalAreas] = useState(false);
  const [isSearchingIndustries, setIsSearchingIndustries] = useState(false);
  const [isSearchingLevels, setIsSearchingLevels] = useState(false);

  // Use API-searched values if search term exists, otherwise use cached filter values
  const filteredCities = useMemo(() => {
    if (citySearch.trim()) {
      return searchedCityValues;
    }
    return Array.isArray(filterValues.cities) ? filterValues.cities : [];
  }, [filterValues.cities, citySearch, searchedCityValues]);

  const filteredFunctionalAreas = useMemo(() => {
    if (functionalAreaSearch.trim()) {
      return searchedFunctionalAreaValues;
    }
    return Array.isArray(filterValues.functionalAreas) ? filterValues.functionalAreas : [];
  }, [filterValues.functionalAreas, functionalAreaSearch, searchedFunctionalAreaValues]);

  const filteredIndustries = useMemo(() => {
    if (industrySearch.trim()) {
      return searchedIndustryValues;
    }
    return Array.isArray(filterValues.industries) ? filterValues.industries : [];
  }, [filterValues.industries, industrySearch, searchedIndustryValues]);

  const filteredLevels = useMemo(() => {
    if (levelSearch.trim()) {
      return searchedLevelValues;
    }
    return Array.isArray(filterValues.levels) ? filterValues.levels : [];
  }, [filterValues.levels, levelSearch, searchedLevelValues]);

  // Load filter values on component mount and refresh
  useEffect(() => {
    const loadFilterValues = async () => {
      try {
        console.log('Loading filter values...');
        const response = await getFilterValues();
        console.log('Filter values loaded:', response.data);
        setFilterValues({
          cities: response.data.cities || [],
          functionalAreas: response.data.functionalAreas || [],
          industries: response.data.industries || [],
          levels: response.data.levels || [],
          genders: response.data.genders || [],
          skills: response.data.skills || []
        });
      } catch (error) {
        console.error('Error loading filter values:', error);
        toast.error('Failed to load filter options');
      }
    };
    
    loadFilterValues();
  }, []);

  // Search cities from API
  useEffect(() => {
    const searchCities = async () => {
      if (!citySearch.trim()) {
        setSearchedCityValues([]);
        setIsSearchingCities(false);
        return;
      }
      
      setIsSearchingCities(true);
      try {
        const results = await searchFilterValues('city', citySearch, 20);
        setSearchedCityValues(results);
      } catch (error) {
        console.error('Error searching cities:', error);
        setSearchedCityValues([]);
      } finally {
        setIsSearchingCities(false);
        // Use requestAnimationFrame to ensure focus happens after render
        requestAnimationFrame(() => {
          citySearchRef.current?.focus();
        });
      }
    };

    const timeoutId = setTimeout(() => {
      searchCities();
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [citySearch]);

  // Search functional areas from API
  useEffect(() => {
    const searchFunctionalAreas = async () => {
      if (!functionalAreaSearch.trim()) {
        setSearchedFunctionalAreaValues([]);
        setIsSearchingFunctionalAreas(false);
        return;
      }
      
      setIsSearchingFunctionalAreas(true);
      try {
        const results = await searchFilterValues('functionalArea', functionalAreaSearch, 20);
        setSearchedFunctionalAreaValues(results);
      } catch (error) {
        console.error('Error searching functional areas:', error);
        setSearchedFunctionalAreaValues([]);
      } finally {
        setIsSearchingFunctionalAreas(false);
        // Use requestAnimationFrame to ensure focus happens after render
        requestAnimationFrame(() => {
          functionalAreaSearchRef.current?.focus();
        });
      }
    };

    const timeoutId = setTimeout(() => {
      searchFunctionalAreas();
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [functionalAreaSearch]);

  // Search industries from API
  useEffect(() => {
    const searchIndustries = async () => {
      if (!industrySearch.trim()) {
        setSearchedIndustryValues([]);
        setIsSearchingIndustries(false);
        return;
      }
      
      setIsSearchingIndustries(true);
      try {
        const results = await searchFilterValues('industry', industrySearch, 20);
        setSearchedIndustryValues(results);
      } catch (error) {
        console.error('Error searching industries:', error);
        setSearchedIndustryValues([]);
      } finally {
        setIsSearchingIndustries(false);
        // Use requestAnimationFrame to ensure focus happens after render
        requestAnimationFrame(() => {
          industrySearchRef.current?.focus();
        });
      }
    };

    const timeoutId = setTimeout(() => {
      searchIndustries();
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [industrySearch]);

  // Search levels from API
  useEffect(() => {
    const searchLevels = async () => {
      if (!levelSearch.trim()) {
        setSearchedLevelValues([]);
        setIsSearchingLevels(false);
        return;
      }
      
      setIsSearchingLevels(true);
      try {
        const results = await searchFilterValues('level', levelSearch, 20);
        setSearchedLevelValues(results);
      } catch (error) {
        console.error('Error searching levels:', error);
        setSearchedLevelValues([]);
      } finally {
        setIsSearchingLevels(false);
        // Use requestAnimationFrame to ensure focus happens after render
        requestAnimationFrame(() => {
          levelSearchRef.current?.focus();
        });
      }
    };

    const timeoutId = setTimeout(() => {
      searchLevels();
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [levelSearch]);

  // Refresh data when window regains focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      // Only refresh if no data is currently loaded
      if (candidates.length === 0 && !loading && !apiCallInProgressRef.current) {
        console.log('Window focused, refreshing candidate data...');
        fetchCandidates(1);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates.length, loading]);

  // Cleanup effect to prevent state updates after unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Auto-expand skills filter when skills are selected
  useEffect(() => {
    if (selectedSkills.length > 0 && !expandedFilters.has('skills')) {
      setExpandedFilters(prev => new Set(prev).add('skills'));
    }
  }, [selectedSkills, expandedFilters]);

  // Manual search trigger - no auto-debounce, user clicks button to search
  // This directly triggers the API call when search button is clicked
  const handleManualSearch = useCallback(() => {
    if (loading || apiCallInProgressRef.current) {
      return;
    }

    // Only trigger search if 3+ characters OR empty (to clear search)
      if (searchTerm.length === 0 || searchTerm.length >= 3) {
      // Update debouncedSearchTerm for consistency
        setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
      
      // Clear any pending debounce
      if (filterDebounceTimeoutRef.current) {
        clearTimeout(filterDebounceTimeoutRef.current);
        filterDebounceTimeoutRef.current = null;
      }
      
      // Update prevFiltersRef to reflect search change
      prevFiltersRef.current = {
        ...prevFiltersRef.current,
        debouncedSearchTerm: searchTerm
      };
      
      // Build API params with current searchTerm (not debouncedSearchTerm which may be stale)
      const apiParams: any = {
        page: 1,
        limit: ITEMS_PER_PAGE
      };

      // Add search term - only if 3+ characters (use current searchTerm, not debounced)
      if (searchTerm.trim().length >= 3) {
        apiParams.search = searchTerm.trim();
      }

      // Add all current filters
      if (selectedGender.length > 0) apiParams.gender = selectedGender;
      if (selectedCity.length > 0) apiParams.city = selectedCity;
      if (selectedFunctionalArea.length > 0) apiParams.functionalArea = selectedFunctionalArea;
      if (selectedIndustry.length > 0) apiParams.industry = selectedIndustry;
      if (selectedLevel.length > 0) apiParams.level = selectedLevel;
      if (selectedSkills.length > 0) apiParams.skills = selectedSkills;
      if (minSalary !== undefined) apiParams.minSalary = minSalary;
      if (maxSalary !== undefined) apiParams.maxSalary = maxSalary;
      if (minExperience !== undefined) apiParams.minExperience = minExperience;
      if (maxExperience !== undefined) apiParams.maxExperience = maxExperience;
      if (minAge !== undefined) apiParams.minAge = minAge;
      if (maxAge !== undefined) apiParams.maxAge = maxAge;

      // Call API directly
      apiCallInProgressRef.current = true;
      setLoading(true);
      
      getCandidateRecords(apiParams)
        .then((response) => {
          if (!isMountedRef.current) {
            apiCallInProgressRef.current = false;
            return;
          }

          setCandidates(response.data.candidates);
          setTotalResults(response.data.totalCount);
          setTotalPages(response.data.totalPages);
          setCurrentPage(1);
          
          // Initialize phone view counts
          const countsMap: { [key: string]: number } = {};
          response.data.candidates.forEach((candidate: Candidate) => {
            if (candidate._id && candidate.phoneViewCount !== undefined) {
              countsMap[candidate._id] = candidate.phoneViewCount;
            }
          });
          setPhoneViewCounts(prev => ({ ...prev, ...countsMap }));
          
          setLoading(false);
          apiCallInProgressRef.current = false;
          
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'auto' });
          }, 50);
        })
        .catch((error) => {
          console.error('Error fetching candidates after search:', error);
          setLoading(false);
          apiCallInProgressRef.current = false;
          toast.error('Failed to fetch candidates');
        });
    }
  }, [searchTerm, loading, selectedGender, selectedCity, selectedFunctionalArea, selectedIndustry, selectedLevel, selectedSkills, minSalary, maxSalary, minExperience, maxExperience, minAge, maxAge]);

  // Auto-trigger API when search is cleared (becomes empty)
  useEffect(() => {
    // Only trigger if:
    // 1. We have an initial fetch done
    // 2. Search term is empty
    // 3. Previous search term was not empty (to avoid triggering on initial load)
    // 4. No API call is in progress
    if (
      hasInitialFetchRef.current &&
      searchTerm.length === 0 &&
      prevFiltersRef.current.debouncedSearchTerm.length > 0 &&
      !apiCallInProgressRef.current &&
      !loading
    ) {
      // Clear search - trigger API call without search param
      setDebouncedSearchTerm('');
      setCurrentPage(1);
      
      // Clear any pending debounce
      if (filterDebounceTimeoutRef.current) {
        clearTimeout(filterDebounceTimeoutRef.current);
        filterDebounceTimeoutRef.current = null;
      }
      
      // Update prevFiltersRef
      prevFiltersRef.current = {
        ...prevFiltersRef.current,
        debouncedSearchTerm: ''
      };
      
      // Build API params without search param (since searchTerm is empty)
      const apiParams: any = {
        page: 1,
        limit: ITEMS_PER_PAGE
      };

      // Add all current filters (no search param since it's empty)
      if (selectedGender.length > 0) apiParams.gender = selectedGender;
      if (selectedCity.length > 0) apiParams.city = selectedCity;
      if (selectedFunctionalArea.length > 0) apiParams.functionalArea = selectedFunctionalArea;
      if (selectedIndustry.length > 0) apiParams.industry = selectedIndustry;
      if (selectedLevel.length > 0) apiParams.level = selectedLevel;
      if (selectedSkills.length > 0) apiParams.skills = selectedSkills;
      if (minSalary !== undefined) apiParams.minSalary = minSalary;
      if (maxSalary !== undefined) apiParams.maxSalary = maxSalary;
      if (minExperience !== undefined) apiParams.minExperience = minExperience;
      if (maxExperience !== undefined) apiParams.maxExperience = maxExperience;
      if (minAge !== undefined) apiParams.minAge = minAge;
      if (maxAge !== undefined) apiParams.maxAge = maxAge;

      // Call API directly without search param
      apiCallInProgressRef.current = true;
      setLoading(true);
      
      getCandidateRecords(apiParams)
        .then((response) => {
          if (!isMountedRef.current) {
            apiCallInProgressRef.current = false;
            return;
          }

          setCandidates(response.data.candidates);
          setTotalResults(response.data.totalCount);
          setTotalPages(response.data.totalPages);
          setCurrentPage(1);
          
          // Initialize phone view counts
          const countsMap: { [key: string]: number } = {};
          response.data.candidates.forEach((candidate: Candidate) => {
            if (candidate._id && candidate.phoneViewCount !== undefined) {
              countsMap[candidate._id] = candidate.phoneViewCount;
            }
          });
          setPhoneViewCounts(prev => ({ ...prev, ...countsMap }));
          
          setLoading(false);
          apiCallInProgressRef.current = false;
          
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'auto' });
          }, 50);
        })
        .catch((error) => {
          console.error('Error fetching candidates after clearing search:', error);
          setLoading(false);
          apiCallInProgressRef.current = false;
          toast.error('Failed to fetch candidates');
        });
    }
  }, [searchTerm, loading, selectedGender, selectedCity, selectedFunctionalArea, selectedIndustry, selectedLevel, selectedSkills, minSalary, maxSalary, minExperience, maxExperience, minAge, maxAge]);

  // Debounce the parent state update to prevent immediate re-renders
  const handleSearchChangeRef = useRef<NodeJS.Timeout>();
  const handleSearchChange = useCallback((value: string) => {
    // Cancel any pending update
    if (handleSearchChangeRef.current) {
      clearTimeout(handleSearchChangeRef.current);
    }
    // Update immediately but use setTimeout to not block input
    handleSearchChangeRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 0);
  }, []);

  // Reusable filter content component with checkboxes
  const FilterContent = useMemo(() => (
    <div className="space-y-3">
      {loading && (
        <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-orange-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Please wait while we fetch candidates...</span>
              </div>
            </div>
      )}
      {/* City Filter */}
      <div className={`border border-gray-200 rounded-lg overflow-hidden ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
        <button
          onClick={() => !loading && toggleFilterExpansion('city')}
          disabled={loading}
          className={`w-full flex items-center justify-between p-3 transition-colors ${loading ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-700 cursor-pointer">City</Label>
            {selectedCity.length > 0 && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-xs px-2 py-0">
                {selectedCity.length}
              </Badge>
            )}
          </div>
          {expandedFilters.has('city') ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {expandedFilters.has('city') && (
          <div className="border-t border-gray-200">
            <div className="p-3">
              <div className="relative">
        <Input
                  ref={citySearchRef}
          placeholder="Search cities..."
          value={citySearch}
                  onChange={(e) => {
                    if (!loading) setCitySearch(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  disabled={loading}
                  className="mb-2 h-8 text-sm pr-8"
                />
                {isSearchingCities && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="px-3 pb-3 space-y-0 max-h-40 overflow-y-auto">
          {isSearchingCities ? (
            <div className="p-2 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-400">Searching...</span>
            </div>
          ) : filteredCities && filteredCities.length > 0 ? (
            filteredCities.map((city, index) => (
              <div key={city}>
              <div className="flex items-center space-x-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    id={`city-${city}`}
                    checked={selectedCity.includes(city)}
                  disabled={loading}
                    onCheckedChange={(checked) => {
                    if (!loading) {
                      if (checked) {
                        setSelectedCity([...selectedCity, city]);
                      } else {
                        setSelectedCity(selectedCity.filter((c) => c !== city));
                      }
                      }
                    }}
                  />
                  <label
                    htmlFor={`city-${city}`}
                    className="text-sm text-gray-500 leading-none cursor-pointer"
                  >
                    {city}
                  </label>
                </div>
                {index < filteredCities.length - 1 && (
                  <div className="border-b border-gray-200 my-1"></div>
                )}
              </div>
            ))
          ) : (
            <div className="p-2 text-sm text-gray-400 text-center">
              {citySearch ? 'No cities found' : 'No cities available'}
            </div>
          )}
        </div>
          </div>
        )}
      </div>

      {/* Functional Area Filter */}
      <div className={`border border-gray-200 rounded-lg overflow-hidden ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
        <button
          onClick={() => !loading && toggleFilterExpansion('functionalArea')}
          disabled={loading}
          className={`w-full flex items-center justify-between p-3 transition-colors ${loading ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-700 cursor-pointer">Functional Area</Label>
            {selectedFunctionalArea.length > 0 && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-xs px-2 py-0">
                {selectedFunctionalArea.length}
              </Badge>
            )}
          </div>
          {expandedFilters.has('functionalArea') ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {expandedFilters.has('functionalArea') && (
          <div className="border-t border-gray-200">
            <div className="p-3">
              <div className="relative">
        <Input
                  ref={functionalAreaSearchRef}
          placeholder="Search functional areas..."
          value={functionalAreaSearch}
                  onChange={(e) => {
                    if (!loading) setFunctionalAreaSearch(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  disabled={loading}
                  className="mb-2 h-8 text-sm pr-8"
                />
                {isSearchingFunctionalAreas && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="px-3 pb-3 space-y-0 max-h-40 overflow-y-auto">
          {isSearchingFunctionalAreas ? (
            <div className="p-2 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-400">Searching...</span>
            </div>
          ) : filteredFunctionalAreas && filteredFunctionalAreas.length > 0 ? (
            filteredFunctionalAreas.map((area, index) => (
              <div key={area}>
              <div className="flex items-center space-x-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    id={`functional-area-${area}`}
                    checked={selectedFunctionalArea.includes(area)}
                  disabled={loading}
                    onCheckedChange={(checked) => {
                    if (!loading) {
                      if (checked) {
                        setSelectedFunctionalArea([...selectedFunctionalArea, area]);
                      } else {
                        setSelectedFunctionalArea(selectedFunctionalArea.filter((a) => a !== area));
                      }
                      }
                    }}
                  />
                  <label
                    htmlFor={`functional-area-${area}`}
                    className="text-sm text-gray-500 leading-none cursor-pointer"
                  >
                    {area}
                  </label>
                </div>
                {index < filteredFunctionalAreas.length - 1 && (
                  <div className="border-b border-gray-200"></div>
                )}
              </div>
            ))
          ) : (
            <div className="p-2 text-sm text-gray-400 text-center">
              {functionalAreaSearch ? 'No functional areas found' : 'No functional areas available'}
            </div>
          )}
        </div>
          </div>
        )}
      </div>

      {/* Industry Filter */}
      <div className={`border border-gray-200 rounded-lg overflow-hidden ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
        <button
          onClick={() => !loading && toggleFilterExpansion('industry')}
          disabled={loading}
          className={`w-full flex items-center justify-between p-3 transition-colors ${loading ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-700 cursor-pointer">Industry</Label>
            {selectedIndustry.length > 0 && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-xs px-2 py-0">
                {selectedIndustry.length}
              </Badge>
            )}
          </div>
          {expandedFilters.has('industry') ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {expandedFilters.has('industry') && (
          <div className="border-t border-gray-200">
            <div className="p-3">
              <div className="relative">
        <Input
                  ref={industrySearchRef}
          placeholder="Search industries..."
          value={industrySearch}
                  onChange={(e) => {
                    if (!loading) setIndustrySearch(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  disabled={loading}
                  className="mb-2 h-8 text-sm pr-8"
                />
                {isSearchingIndustries && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="px-3 pb-3 space-y-0 max-h-40 overflow-y-auto">
          {isSearchingIndustries ? (
            <div className="p-2 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-400">Searching...</span>
            </div>
          ) : filteredIndustries && filteredIndustries.length > 0 ? (
            filteredIndustries.map((industry, index) => (
              <div key={industry}>
              <div className="flex items-center space-x-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    id={`industry-${industry}`}
                    checked={selectedIndustry.includes(industry)}
                  disabled={loading}
                    onCheckedChange={(checked) => {
                    if (!loading) {
                      if (checked) {
                        setSelectedIndustry([...selectedIndustry, industry]);
                      } else {
                        setSelectedIndustry(selectedIndustry.filter((i) => i !== industry));
                      }
                      }
                    }}
                  />
                  <label
                    htmlFor={`industry-${industry}`}
                    className="text-sm text-gray-500 leading-none cursor-pointer"
                  >
                    {industry}
                  </label>
                </div>
                {index < filteredIndustries.length - 1 && (
                  <div className="border-b border-gray-200"></div>
                )}
              </div>
            ))
          ) : (
            <div className="p-2 text-sm text-gray-400 text-center">
              {industrySearch ? 'No industries found' : 'No industries available'}
            </div>
          )}
        </div>
          </div>
        )}
      </div>

      {/* Level Filter */}
      <div className={`border border-gray-200 rounded-lg overflow-hidden ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
        <button
          onClick={() => !loading && toggleFilterExpansion('level')}
          disabled={loading}
          className={`w-full flex items-center justify-between p-3 transition-colors ${loading ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-700 cursor-pointer">Level</Label>
            {selectedLevel.length > 0 && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-xs px-2 py-0">
                {selectedLevel.length}
              </Badge>
            )}
          </div>
          {expandedFilters.has('level') ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {expandedFilters.has('level') && (
          <div className="border-t border-gray-200">
            <div className="p-3">
              <div className="relative">
        <Input
                  ref={levelSearchRef}
          placeholder="Search levels..."
          value={levelSearch}
                  onChange={(e) => {
                    if (!loading) setLevelSearch(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  disabled={loading}
                  className="mb-2 h-8 text-sm pr-8"
                />
                {isSearchingLevels && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="px-3 pb-3 space-y-0 max-h-40 overflow-y-auto">
          {isSearchingLevels ? (
            <div className="p-2 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-400">Searching...</span>
            </div>
          ) : filteredLevels && filteredLevels.length > 0 ? (
            filteredLevels.map((level, index) => (
              <div key={level}>
              <div className="flex items-center space-x-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    id={`level-${level}`}
                    checked={selectedLevel.includes(level)}
                  disabled={loading}
                    onCheckedChange={(checked) => {
                    if (!loading) {
                      if (checked) {
                        setSelectedLevel([...selectedLevel, level]);
                      } else {
                        setSelectedLevel(selectedLevel.filter((l) => l !== level));
                      }
                      }
                    }}
                  />
                  <label
                    htmlFor={`level-${level}`}
                    className="text-sm text-gray-500 leading-none cursor-pointer"
                  >
                    {level}
                  </label>
                </div>
                {index < filteredLevels.length - 1 && (
                  <div className="border-b border-gray-200 my-1"></div>
                )}
              </div>
            ))
          ) : (
            <div className="p-2 text-sm text-gray-400 text-center">
              {levelSearch ? 'No levels found' : 'No levels available'}
            </div>
          )}
        </div>
          </div>
        )}
      </div>

      {/* Skills Filter */}
      <div className={`border border-gray-200 rounded-lg overflow-hidden ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
        <button
          onClick={() => !loading && toggleFilterExpansion('skills')}
          disabled={loading}
          className={`w-full flex items-center justify-between p-3 transition-colors ${loading ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-700 cursor-pointer">Skills</Label>
            {selectedSkills.length > 0 && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-xs px-2 py-0">
                {selectedSkills.length}
              </Badge>
            )}
          </div>
          {expandedFilters.has('skills') ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {expandedFilters.has('skills') && (
          <div className="border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
            <div className="p-3">
              <Input
                ref={skillsSearchRef}
                placeholder="Type skill name and press Enter..."
                value={skillsSearch}
                onChange={(e) => {
                  if (!loading) setSkillsSearch(e.target.value);
                }}
                disabled={loading}
                onKeyDown={(e) => {
                  if (!loading && e.key === 'Enter') {
                    e.preventDefault();
                    if (skillsSearch.trim() && !selectedSkills.includes(skillsSearch.trim())) {
                      setSelectedSkills([...selectedSkills, skillsSearch.trim()]);
                      setSkillsSearch('');
                      // Focus back on the input after adding
                      setTimeout(() => {
                        skillsSearchRef.current?.focus();
                      }, 0);
                    }
                  }
                }}
                className="h-8 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Press Enter to add a skill</p>
            </div>
            <div className="px-3 pb-3 space-y-0 max-h-60 overflow-y-auto">
              {selectedSkills.length > 0 ? (
                selectedSkills.map((skill, index) => (
                  <div key={skill}>
                    <div className="flex items-center justify-between py-2 px-2 hover:bg-gray-50 rounded" onClick={(e) => e.stopPropagation()}>
                      <span className="text-sm text-gray-700">{skill}</span>
                      <button
                        onClick={() => {
                          if (!loading) {
                            setSelectedSkills(selectedSkills.filter((s) => s !== skill));
                            // Refocus the input after removing a skill
                            setTimeout(() => {
                              skillsSearchRef.current?.focus();
                            }, 0);
                          }
                        }}
                        disabled={loading}
                        className="text-red-500 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={`Remove ${skill} skill`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    {index < selectedSkills.length - 1 && (
                      <div className="border-b border-gray-200"></div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-4 text-sm text-gray-400 text-center">
                  No skills added yet
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Salary Range Filter */}
      <div className={`border border-gray-200 rounded-lg overflow-hidden ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
        <button
          onClick={() => !loading && toggleFilterExpansion('salary')}
          disabled={loading}
          className={`w-full flex items-center justify-between p-3 transition-colors ${loading ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-700 cursor-pointer">Salary Range</Label>
            {(minSalary !== undefined || maxSalary !== undefined) && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-xs px-2 py-0">
                1
              </Badge>
            )}
          </div>
          {expandedFilters.has('salary') ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {expandedFilters.has('salary') && (
          <div className="border-t border-gray-200">
            <div className="p-4 space-y-4">
              {/* Salary Range Input Fields */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700 mb-3 block">
                  Select Salary Range
                </Label>
                
                {/* Quick Select Buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { min: 0, max: 10000, label: '0 - 10K' },
                    { min: 10000, max: 50000, label: '10K - 50K' },
                    { min: 50000, max: 100000, label: '50K - 1L' },
                    { min: 100000, max: 200000, label: '1L - 2L' },
                    { min: 200000, max: 500000, label: '2L - 5L' },
                    { min: 500000, max: 2000000, label: '5L+' },
                  ].map((range) => (
                    <button
                      key={range.label}
                      onClick={() => {
                        if (!loading) {
                          setMinSalary(range.min);
                          setMaxSalary(range.max);
                        }
                      }}
                      disabled={loading}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
                
                {/* Input Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1.5 block">From (₹)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={minSalary !== undefined ? minSalary : ''}
                      onChange={(e) => {
                        if (!loading) {
                          const value = e.target.value ? parseInt(e.target.value) : undefined;
                          setMinSalary(value);
                        }
                      }}
                      disabled={loading}
                      className="h-9 text-sm"
                      min={0}
                      max={500000}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1.5 block">To (₹)</Label>
                    <Input
                      type="number"
                      placeholder="500000"
                      value={maxSalary !== undefined ? maxSalary : ''}
                      onChange={(e) => {
                        if (!loading) {
                          const value = e.target.value ? parseInt(e.target.value) : undefined;
                          setMaxSalary(value);
                        }
                      }}
                      disabled={loading}
                      className="h-9 text-sm"
                      min={0}
                      max={500000}
                    />
                  </div>
                </div>
              </div>
              
              {/* Submit Button */}
              <Button
                onClick={applyFilters}
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Applying...
                  </>
                ) : (
                  'Apply Filters'
                )}
              </Button>
              
              {(minSalary !== undefined || maxSalary !== undefined) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!loading && !apiCallInProgressRef.current) {
                      setMinSalary(undefined);
                      setMaxSalary(undefined);
                      setCurrentPage(1);
                      // The useEffect will automatically detect the change and trigger the API call
                    }
                  }}
                  disabled={loading}
                  className="w-full text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear Salary Range
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Work Experience Filter */}
      <div className={`border border-gray-200 rounded-lg overflow-hidden ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
        <button
          onClick={() => !loading && toggleFilterExpansion('experience')}
          disabled={loading}
          className={`w-full flex items-center justify-between p-3 transition-colors ${loading ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-700 cursor-pointer">Work Experience</Label>
            {(minExperience !== undefined || maxExperience !== undefined) && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-xs px-2 py-0">
                1
              </Badge>
            )}
          </div>
          {expandedFilters.has('experience') ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {expandedFilters.has('experience') && (
          <div className="border-t border-gray-200">
            <div className="p-4 space-y-4">
              {/* Work Experience Input Fields */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700 mb-3 block">
                  Select Work Experience Range
                </Label>
                
                {/* Quick Select Buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { min: 0, max: 2, label: '0 - 2 Yrs' },
                    { min: 2, max: 5, label: '2 - 5 Yrs' },
                    { min: 5, max: 10, label: '5 - 10 Yrs' },
                    { min: 10, max: 15, label: '10 - 15 Yrs' },
                    { min: 15, max: 30, label: '15+ Yrs' },
                  ].map((range) => (
                    <button
                      key={range.label}
                      onClick={() => {
                        if (!loading) {
                          setMinExperience(range.min);
                          setMaxExperience(range.max);
                        }
                      }}
                      disabled={loading}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
                
                {/* Input Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1.5 block">From (Yrs)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={minExperience !== undefined ? minExperience : ''}
                      onChange={(e) => {
                        if (!loading) {
                          const value = e.target.value ? parseInt(e.target.value) : undefined;
                          setMinExperience(value);
                        }
                      }}
                      disabled={loading}
                      className="h-9 text-sm"
                      min={0}
                      max={50}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1.5 block">To (Yrs)</Label>
                    <Input
                      type="number"
                      placeholder="50"
                      value={maxExperience !== undefined ? maxExperience : ''}
                      onChange={(e) => {
                        if (!loading) {
                          const value = e.target.value ? parseInt(e.target.value) : undefined;
                          setMaxExperience(value);
                        }
                      }}
                      disabled={loading}
                      className="h-9 text-sm"
                      min={0}
                      max={50}
                    />
                  </div>
                </div>
              </div>
              
              {/* Submit Button */}
              <Button
                onClick={applyFilters}
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Applying...
                  </>
                ) : (
                  'Apply Filters'
                )}
              </Button>
              
                  {(minExperience !== undefined || maxExperience !== undefined) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!loading && !apiCallInProgressRef.current) {
                      setMinExperience(undefined);
                      setMaxExperience(undefined);
                      setCurrentPage(1);
                      // The useEffect will automatically detect the change and trigger the API call
                    }
                  }}
                  disabled={loading}
                  className="w-full text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear Experience Range
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Age Filter */}
      <div className={`border border-gray-200 rounded-lg overflow-hidden ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
        <button
          onClick={() => !loading && toggleFilterExpansion('age')}
          disabled={loading}
          className={`w-full flex items-center justify-between p-3 transition-colors ${loading ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
        >
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-700 cursor-pointer">Age</Label>
            {(minAge !== undefined || maxAge !== undefined) && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-xs px-2 py-0">
                1
              </Badge>
            )}
          </div>
          {expandedFilters.has('age') ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {expandedFilters.has('age') && (
          <div className="border-t border-gray-200">
            <div className="p-4 space-y-4">
              {/* Age Input Fields */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700 mb-3 block">
                  Select Age Range
                </Label>
                
                {/* Quick Select Buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { min: 18, max: 25, label: '18 - 25 Yrs' },
                    { min: 25, max: 35, label: '25 - 35 Yrs' },
                    { min: 35, max: 45, label: '35 - 45 Yrs' },
                    { min: 45, max: 60, label: '45 - 60 Yrs' },
                    { min: 60, max: 100, label: '60+ Yrs' },
                  ].map((range) => (
                    <button
                      key={range.label}
                      onClick={() => {
                        if (!loading) {
                          setMinAge(range.min);
                          setMaxAge(range.max);
                        }
                      }}
                      disabled={loading}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
                
                {/* Input Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1.5 block">From (Yrs)</Label>
                    <Input
                      type="number"
                      placeholder="18"
                      value={minAge !== undefined ? minAge : ''}
                      onChange={(e) => {
                        if (!loading) {
                          const value = e.target.value ? parseInt(e.target.value) : undefined;
                          setMinAge(value);
                        }
                      }}
                      disabled={loading}
                      className="h-9 text-sm"
                      min={18}
                      max={100}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1.5 block">To (Yrs)</Label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={maxAge !== undefined ? maxAge : ''}
                      onChange={(e) => {
                        if (!loading) {
                          const value = e.target.value ? parseInt(e.target.value) : undefined;
                          setMaxAge(value);
                        }
                      }}
                      disabled={loading}
                      className="h-9 text-sm"
                      min={18}
                      max={100}
                    />
                  </div>
                </div>
              </div>
              
              {/* Submit Button */}
              <Button
                onClick={applyFilters}
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Applying...
                  </>
                ) : (
                  'Apply Filters'
                )}
              </Button>
              
              {(minAge !== undefined || maxAge !== undefined) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!loading && !apiCallInProgressRef.current) {
                      setMinAge(undefined);
                      setMaxAge(undefined);
                      setCurrentPage(1);
                      // The useEffect will automatically detect the change and trigger the API call
                    }
                  }}
                  disabled={loading}
                  className="w-full text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear Age Range
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-gray-200">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={clearAllFilters} 
          disabled={loading}
          className="w-full"
        >
          Clear All Filters
        </Button>
      </div>
    </div>
   ), [expandedFilters, selectedGender, selectedCity, selectedFunctionalArea, selectedIndustry, selectedLevel, selectedSkills, minSalary, maxSalary, minExperience, maxExperience, minAge, maxAge, citySearch, functionalAreaSearch, industrySearch, levelSearch, skillsSearch, filteredCities, filteredFunctionalAreas, filteredIndustries, filteredLevels, isSearchingCities, searchedCityValues, isSearchingFunctionalAreas, searchedFunctionalAreaValues, isSearchingIndustries, searchedIndustryValues, isSearchingLevels, searchedLevelValues, toggleFilterExpansion, clearAllFilters, loading, applyFilters]);

  return (  
    <div className="min-h-screen flex flex-col">
      <Card className="shadow-lg flex flex-row flex-1 relative">
      <section className={`flex-col transition-all duration-300 ${
        showFilters ? 'w-full md:w-full lg:w-[77%]' : 'w-full'
      }`}>
        <CardHeader className="flex-shrink-0 w-full p-3 sm:p-4 md:p-6">
          <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 truncate">
                  Candidate Database
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
                  Manage and search candidate information
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Show filter button only on md and smaller screens */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // For mobile, toggle mobile filter
                  if (window.innerWidth < 768) {
                    setIsMobileFilterOpen(!isMobileFilterOpen);
                  } else {
                    // For md screens, toggle regular filter
                    setShowFilters(!showFilters);
                  }
                }}
                className="border-orange-300 text-orange-600 hover:bg-orange-50 md:flex lg:hidden p-2"
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Search Bar - Isolated component to prevent parent re-renders */}
          <SearchBox 
            onSearchChange={handleSearchChange} 
            onSearchClick={handleManualSearch}
            searchTerm={searchTerm}
            loading={loading}
          />


          {activeFilters.length > 0 && (
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap mt-2 sm:mt-3">
              <span className="text-xs sm:text-sm text-muted-foreground">Active filters:</span>
              {activeFilters.map((filter) => 
                filter.values.map((value: string) => (
                  <Badge key={`${filter.key}-${value}`} variant="secondary" className="flex items-center gap-1 text-xs px-2 py-1">
                    <span className="hidden xs:inline">{filter.label}:</span>
                    <span className="truncate max-w-20 sm:max-w-none">{value}</span>
                    <X
                      className="w-2 h-2 sm:w-3 sm:h-3 cursor-pointer flex-shrink-0 hover:text-red-500 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        
                        if (loading || apiCallInProgressRef.current) {
                          return;
                        }
                        
                        // Reset to page 1 when any filter changes
                        setCurrentPage(1);
                        
                        // Clear any pending debounce to ensure immediate API call
                        if (filterDebounceTimeoutRef.current) {
                          clearTimeout(filterDebounceTimeoutRef.current);
                          filterDebounceTimeoutRef.current = null;
                        }
                        
                        // Compute updated filter values immediately (before state update)
                        let updatedCity = selectedCity;
                        let updatedFunctionalArea = selectedFunctionalArea;
                        let updatedIndustry = selectedIndustry;
                        let updatedLevel = selectedLevel;
                        let updatedSkills = selectedSkills;
                        let updatedMinSalary = minSalary;
                        let updatedMaxSalary = maxSalary;
                        let updatedMinExperience = minExperience;
                        let updatedMaxExperience = maxExperience;
                        let updatedMinAge = minAge;
                        let updatedMaxAge = maxAge;
                        
                        // Update filter state and compute new values
                        if (filter.key === 'city') {
                          updatedCity = selectedCity.filter((v: string) => v !== value);
                          setSelectedCity(updatedCity);
                        } else if (filter.key === 'functionalArea') {
                          updatedFunctionalArea = selectedFunctionalArea.filter((v: string) => v !== value);
                          setSelectedFunctionalArea(updatedFunctionalArea);
                        } else if (filter.key === 'industry') {
                          updatedIndustry = selectedIndustry.filter((v: string) => v !== value);
                          setSelectedIndustry(updatedIndustry);
                        } else if (filter.key === 'level') {
                          updatedLevel = selectedLevel.filter((v: string) => v !== value);
                          setSelectedLevel(updatedLevel);
                        } else if (filter.key === 'skills') {
                          updatedSkills = selectedSkills.filter((v: string) => v !== value);
                          setSelectedSkills(updatedSkills);
                        } else if (filter.key === 'salary') {
                          updatedMinSalary = undefined;
                          updatedMaxSalary = undefined;
                          setMinSalary(undefined);
                          setMaxSalary(undefined);
                        } else if (filter.key === 'experience') {
                          updatedMinExperience = undefined;
                          updatedMaxExperience = undefined;
                          setMinExperience(undefined);
                          setMaxExperience(undefined);
                        } else if (filter.key === 'age') {
                          updatedMinAge = undefined;
                          updatedMaxAge = undefined;
                          setMinAge(undefined);
                          setMaxAge(undefined);
                        }
                        
                        // Update prevFiltersRef with new values
                        prevFiltersRef.current = {
                          ...prevFiltersRef.current,
                          selectedCity: updatedCity,
                          selectedFunctionalArea: updatedFunctionalArea,
                          selectedIndustry: updatedIndustry,
                          selectedLevel: updatedLevel,
                          selectedSkills: updatedSkills,
                          minSalary: updatedMinSalary,
                          maxSalary: updatedMaxSalary,
                          minExperience: updatedMinExperience,
                          maxExperience: updatedMaxExperience,
                          minAge: updatedMinAge,
                          maxAge: updatedMaxAge
                        };
                        
                        // Build API params with updated filter values
                        const apiParams: any = {
                          page: 1,
                          limit: ITEMS_PER_PAGE
                        };

                        // Add search term - only if 3+ characters
                        if (debouncedSearchTerm && debouncedSearchTerm.trim().length >= 3) {
                          apiParams.search = debouncedSearchTerm.trim();
                        }

                        // Add filters (arrays for multi-select) using updated values
                        if (updatedCity.length > 0) apiParams.city = updatedCity;
                        if (updatedFunctionalArea.length > 0) apiParams.functionalArea = updatedFunctionalArea;
                        if (updatedIndustry.length > 0) apiParams.industry = updatedIndustry;
                        if (updatedLevel.length > 0) apiParams.level = updatedLevel;
                        if (updatedSkills.length > 0) apiParams.skills = updatedSkills;
                        if (selectedGender.length > 0) apiParams.gender = selectedGender;
                        if (updatedMinSalary !== undefined) apiParams.minSalary = updatedMinSalary;
                        if (updatedMaxSalary !== undefined) apiParams.maxSalary = updatedMaxSalary;
                        if (updatedMinExperience !== undefined) apiParams.minExperience = updatedMinExperience;
                        if (updatedMaxExperience !== undefined) apiParams.maxExperience = updatedMaxExperience;
                        if (updatedMinAge !== undefined) apiParams.minAge = updatedMinAge;
                        if (updatedMaxAge !== undefined) apiParams.maxAge = updatedMaxAge;

                        // Call API directly with updated params
                        apiCallInProgressRef.current = true;
                        setLoading(true);
                        
                        getCandidateRecords(apiParams)
                          .then((response) => {
                            if (!isMountedRef.current) {
                              apiCallInProgressRef.current = false;
                              return;
                            }

                            setCandidates(response.data.candidates);
                            setTotalResults(response.data.totalCount);
                            setTotalPages(response.data.totalPages);
                        setCurrentPage(1);
                            
                            // Initialize phone view counts
                            const countsMap: { [key: string]: number } = {};
                            response.data.candidates.forEach((candidate: Candidate) => {
                              if (candidate._id && candidate.phoneViewCount !== undefined) {
                                countsMap[candidate._id] = candidate.phoneViewCount;
                              }
                            });
                            setPhoneViewCounts(prev => ({ ...prev, ...countsMap }));
                            
                            setLoading(false);
                            apiCallInProgressRef.current = false;
                            
                            setTimeout(() => {
                              window.scrollTo({ top: 0, behavior: 'auto' });
                            }, 50);
                          })
                          .catch((error) => {
                            console.error('Error fetching candidates after filter removal:', error);
                            setLoading(false);
                            apiCallInProgressRef.current = false;
                            toast.error('Failed to fetch candidates');
                          });
                      }}
                    />
                  </Badge>
                ))
              )}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs px-2 py-1 hover:text-red-500 hover:bg-red-50 transition-colors">
                Clear all
              </Button>
            </div>
          )}
        </CardHeader>

        <CardContent className="flex flex-col flex-1 min-h-0 p-0">
          <div className="bg-background relative flex-1 flex min-h-0">
            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-auto">
                {/* Candidate Cards View */}
                <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6">
                {loading ? (
                  // Shimmer loading cards
                  Array.from({ length: 5 }).map((_, index) => (
                    <div key={`shimmer-${index}`} className="animate-pulse bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex gap-4">
                        <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                        <div className="flex-1 space-y-3">
                          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : filteredCandidates.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    {searchTerm || activeFilters.length > 0 ? 'No candidates found matching your criteria.' : 'No candidates available.'}
                  </div>
                ) : (
                  filteredCandidates.map((candidate) => (
                    <div key={candidate._id} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Profile Picture */}
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                            {(candidate.name || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                          </div>
                        </div>

                        {/* Candidate Details */}
                        <div className="flex-1 min-w-0">
                          {/* Header */}
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4 gap-2 sm:gap-0">
                            <div className="flex-1">
                              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                                {highlightAllMatches(candidate.name || 'Not specified', searchTerm)}
                              </h3>
                              <span className="flex items-center gap-1 mb-2">
                                <svg
                                  className="w-4 h-4 text-gray-500"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m0 0v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8m18 0L12 13 3 8"
                                  />
                                </svg>
                                <h2 className="text-sm sm:text-base text-orange-600 cursor-pointer hover:underline hover:text-orange-700" onClick={() => candidate.email && window.open(`mailto:${candidate.email?.toLowerCase()}`, '_blank')}>
                                  {highlightAllMatches(candidate.email?.toLowerCase() || 'Not specified', searchTerm)}
                                </h2>
                              </span>
                              {/* <p className="text-xs sm:text-sm text-gray-600 mb-2">
                                {highlightAllMatches(candidate.resumeTitle || 'Professional', searchTerm)}
                              </p> */}
                              {candidate.functionalArea && (
                                <p className="text-xs sm:text-sm text-gray-500 mb-2">
                                  Functional Area: {highlightAllMatches(candidate.functionalArea, searchTerm)}
                                </p>
                              )}
                              {candidate.industry && (
                                <p className="text-xs sm:text-sm text-gray-500 mb-2">
                                  Industry: {highlightAllMatches(candidate.industry, searchTerm)}
                                </p>
                              )}
                              {candidate.areaOfSpecialization && (
                                <p className="text-xs sm:text-sm text-gray-500 mb-2">
                                  Area of Specialization: {highlightAllMatches(candidate.areaOfSpecialization, searchTerm)}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  {/* <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 01-2 2H10a2 2 0 01-2-2V6m8 0V4a2 2 0 00-2-2H10a2 2 0 00-2-2v2" />
                                  </svg> */}
                                  <Briefcase className="w-4 h-4" />
                                  {candidate.workExperience || 0} Yrs
                                </span>
                                <span className="flex items-center gap-1">
                                  {/* <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                  </svg> */}
                                  <IndianRupee className="w-4 h-4" />

                                  {highlightAllMatches(candidate?.salary?.toFixed(2) || ' Salary not available', searchTerm)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  {highlightAllMatches(candidate.city || 'Location not specified', searchTerm)}
                                </span>
                                {/* Phone Number Display */}
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>

                                  {showPhoneNumbers[candidate._id!] ? (
                                    <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                          copyToClipboard(candidate.mobile || "");
                                    }}
                                    className="text-orange-600 hover:text-orange-700 font-medium cursor-pointer focus:outline-none hover:underline"
                                        title="Click to copy"
                                  >
                                    {highlightAllMatches(candidate.mobile || "Contact not available", searchTerm)}
                                  </button>
                                      <button
                                        onClick={() => {
                                          togglePhoneNumber(candidate._id!);
                                        }}
                                        className="text-xs text-gray-500 hover:text-gray-700 underline ml-1"
                                        title="Click to hide"
                                      >
                                        (hide)
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        togglePhoneNumber(candidate._id!);
                                      }}
                                      className="text-orange-600 hover:text-orange-700 font-medium cursor-pointer focus:outline-none hover:underline"
                                    >
                                      Click to show mobile
                                    </button>
                                  )}
                                </span>
                                {/* Address Display */}
                                {candidate?.address && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                    </svg>
                                    {highlightAllMatches(candidate.address, searchTerm)}
                                  </span>
                                )}
                                {/* Preferred Location Display */}
                                {candidate?.preferredLocation && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Preferred: {highlightAllMatches(candidate.preferredLocation, searchTerm)}
                                  </span>
                                )}
                                {/* DOB Display */}
                                {candidate?.dob && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>
                                    {highlightAllMatches(new Date(candidate.dob).toLocaleDateString(), searchTerm)}
                                      {(() => {
                                        const age = calculateAge(candidate.dob);
                                        return age !== null ? ` (${age} years)` : '';
                                      })()}
                                    </span>
                                  </span>
                                )}
                                {/* Gender Display */}
                                {candidate?.gender && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    {highlightAllMatches(candidate.gender, searchTerm)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:flex-col items-start sm:items-end gap-2">
                              <Badge className={`${getStatusInfo(candidate.level || candidate.gender || 'Unknown').className} text-xs px-2 py-1`}>
                                {getStatusInfo(candidate.level || candidate.gender || 'Unknown').label}
                              </Badge>
                            </div>
                          </div>

                          {/* Summary Section */}
                          <div className="mb-4">
                            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                              <button
                                onClick={() => toggleSection(candidate._id!, 'summary')}
                                className="w-full flex items-center justify-between p-3 hover:bg-gray-100 transition-colors"
                              >
                                <p className="text-sm font-semibold text-gray-800">Summary</p>
                                {isSectionExpanded(candidate._id!, 'summary') ? (
                                  <ChevronUp className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-gray-500" />
                                )}
                              </button>
                              {isSectionExpanded(candidate._id!, 'summary') && (
                                <div className="border-t border-gray-200 p-3">
                              <p className="text-sm text-gray-700 mb-2">
                                <span className="font-medium">Role Summary:</span> Professional with {highlightAllMatches(`${candidate.workExperience || 0} years experience`, searchTerm)}
                              </p>
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">Technical Skills Summary:</span> {highlightAllMatches((candidate.keySkills || []).slice(0, 4).join(', '), searchTerm)}{(candidate.keySkills || []).length > 4 && '...'}
                              </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Educational Details Section */}
                          {(candidate.courseHighestEducation || candidate.instituteHighestEducation || candidate.specializationHighestEducation || candidate.courseSecondHighestEducation || candidate.instituteSecondHighestEducation || candidate.specializationSecondHighestEducation) && (
                            <div className="mb-4">
                              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                <button
                                  onClick={() => toggleSection(candidate._id!, 'education')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-gray-100 transition-colors"
                                >
                                  <p className="text-sm font-semibold text-gray-800">Educational Details</p>
                                  {isSectionExpanded(candidate._id!, 'education') ? (
                                    <ChevronUp className="h-4 w-4 text-gray-500" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4 text-gray-500" />
                                  )}
                                </button>
                                {isSectionExpanded(candidate._id!, 'education') && (
                                  <div className="border-t border-gray-200 p-3 sm:p-4">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                  {/* Highest Education */}
                                  {(candidate.courseHighestEducation || candidate.instituteHighestEducation || candidate.specializationHighestEducation) && (
                                    <div className="bg-white rounded-md p-3 sm:p-4 border border-gray-200">
                                      <p className="text-xs font-semibold text-gray-700 mb-2">Highest Qualification</p>
                                      <div className="space-y-1.5">
                                        {candidate.courseHighestEducation && (
                                          <p className="text-sm text-gray-700">
                                            <span className="font-medium text-gray-600">Degree:</span> {highlightAllMatches(candidate.courseHighestEducation, searchTerm)}
                                          </p>
                                        )}
                                        {candidate.specializationHighestEducation && (
                                          <p className="text-sm text-gray-700">
                                            <span className="font-medium text-gray-600">Specialization:</span> {highlightAllMatches(candidate.specializationHighestEducation, searchTerm)}
                                          </p>
                                        )}
                                        {candidate.instituteHighestEducation && (
                                          <p className="text-sm text-gray-700">
                                            <span className="font-medium text-gray-600">Institute:</span> {highlightAllMatches(candidate.instituteHighestEducation, searchTerm)}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Second Highest Education */}
                                  {(candidate.courseSecondHighestEducation || candidate.instituteSecondHighestEducation || candidate.specializationSecondHighestEducation) && (
                                    <div className="bg-white rounded-md p-3 sm:p-4 border border-gray-200">
                                      <p className="text-xs font-semibold text-gray-700 mb-2">Additional Qualification</p>
                                      <div className="space-y-1.5">
                                        {candidate.courseSecondHighestEducation && (
                                          <p className="text-sm text-gray-700">
                                            <span className="font-medium text-gray-600">Degree:</span> {highlightAllMatches(candidate.courseSecondHighestEducation, searchTerm)}
                                          </p>
                                        )}
                                        {candidate.specializationSecondHighestEducation && (
                                          <p className="text-sm text-gray-700">
                                            <span className="font-medium text-gray-600">Specialization:</span> {highlightAllMatches(candidate.specializationSecondHighestEducation, searchTerm)}
                                          </p>
                                        )}
                                        {candidate.instituteSecondHighestEducation && (
                                          <p className="text-sm text-gray-700">
                                            <span className="font-medium text-gray-600">Institute:</span> {highlightAllMatches(candidate.instituteSecondHighestEducation, searchTerm)}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Details Grid */}
                          <div className="mb-4">
                            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                              <button
                                onClick={() => toggleSection(candidate._id!, 'details')}
                                className="w-full flex items-center justify-between p-3 hover:bg-gray-100 transition-colors"
                              >
                                <p className="text-sm font-semibold text-gray-800">Additional Details</p>
                                {isSectionExpanded(candidate._id!, 'details') ? (
                                  <ChevronUp className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-gray-500" />
                                )}
                              </button>
                              {isSectionExpanded(candidate._id!, 'details') && (
                                <div className="border-t border-gray-200 p-3 sm:p-4">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                            <div className="space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-gray-600"> Current Company:</span>
                                <p className="text-gray-900">{highlightAllMatches(candidate.company || candidate.functionalArea || 'Company not specified', searchTerm)}</p>
                              </div>
                              {false && (
                                <div className="text-sm">
                                  <span className="font-medium text-gray-600">Previous:</span>
                                  <p className="text-gray-900">Previous experience available</p>
                                </div>
                              )}
                            </div>

                            <div className="space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-gray-600">Education:</span>
                                <p className="text-gray-900">{highlightAllMatches(candidate.courseHighestEducation || candidate.instituteHighestEducation || 'Education not specified', searchTerm)}</p>
                              </div>
                              {false && (
                                <div className="text-sm">
                                  <span className="font-medium text-gray-600">Pref Loc:</span>
                                  <p className="text-gray-900">{candidate.preferredLocation || candidate.city || 'Location not specified'}</p>
                                </div>
                              )}
                            </div>

                            <div className="space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-gray-600">Key Skills:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(candidate.keySkills || []).slice(0, 6).map((skill: string, index: number) => (
                                    <Badge key={index} variant="secondary" className="text-xs">
                                      {highlightAllMatches(skill, searchTerm)}
                                    </Badge>
                                  ))}
                                  {(candidate.keySkills || []).length > 6 && (
                                    <Badge variant="secondary" className="text-xs">
                                      +{(candidate.keySkills || []).length - 6} more
                                    </Badge>
                                  )}
                                </div>
                              </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Footer Metrics */}
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-4 border-t border-gray-200 gap-3 sm:gap-0">
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>Active: {candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString() : 'N/A'}</span>
                              <span>Modified: {candidate.updatedAt ? new Date(candidate.updatedAt).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFlagConfirmCandidateId(candidate._id);
                                }}
                                className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors flex-shrink-0 ${
                                  candidate.flagged
                                    ? 'bg-red-50 border border-red-300 text-red-600 hover:bg-red-100'
                                    : 'bg-gray-50 border border-gray-300 text-gray-600 hover:bg-gray-100'
                                }`}
                                title={candidate.flagged ? 'Remove flag' : 'Flag candidate'}
                              >
                                <Flag className="h-4 w-4" fill={candidate.flagged ? 'currentColor' : 'none'} />{candidate.flagged ? '' : ''}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const candidateSkills: any = (candidate as any)?.keySkills;
                                  setSelectedCandidateForApproach({
                                    id: candidate._id,
                                    name: candidate.name,
                                    email: candidate.email || '',
                                    phone: candidate.mobile || '',
                                    highestQualification: candidate.courseHighestEducation || '',
                                    profileSource: '',
                                    dob: candidate.dob || '',
                                    skills: Array.isArray(candidateSkills) ? candidateSkills : [],
                                    workExperience: candidate.workExperience || 0 
                                  });
                                  setShowApproachModal(true);
                                }}
                                className="h-8 px-2 sm:px-3 bg-blue-50 border border-blue-300 text-blue-600 hover:bg-blue-100 rounded-md transition-colors flex items-center gap-1.5 sm:gap-2"
                                title="Add Approach"
                              >
                                <Plus className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden sm:inline whitespace-nowrap text-sm font-medium">
                                  Add Approach
                                </span>
                              </button>
                              <button
                                onClick={async () => {
                                  if (!showPhoneNumbers[candidate._id!]) {
                                    await togglePhoneNumber(candidate._id!);
                                  } else {
                                    togglePhoneNumber(candidate._id!);
                                  }
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-md transition-colors"
                                title="Show phone number"
                              >
                                <Eye className="h-4 w-4" />
                                <span className="font-medium">
                                  {phoneViewCounts[candidate._id!] !== undefined 
                                    ? phoneViewCounts[candidate._id!] 
                                    : candidate.phoneViewCount || 0}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                
                {/* Pagination UI */}
                {totalPages > 1 && (
                  <div className="flex flex-col items-center gap-4 py-6">
                    <div className="flex items-center gap-2 flex-wrap justify-center">
                      {/* Previous Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1 || loading}
                        className="border-orange-300 text-orange-600 hover:bg-orange-50"
                      >
                        Previous
                      </Button>
                      
                      {/* Page Numbers */}
                      {pageButtons}
                      
                      {/* Next Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages || loading}
                        className="border-orange-300 text-orange-600 hover:bg-orange-50"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>

                  </div>
        </CardContent>
      </section>
        
        
            {/* Filter Sidebar - Responsive */}
            {/* Large screens: Always visible */}
            <section className="hidden lg:block w-[23%] h-[calc(100vh-10px)] bg-gray-100 overflow-y-auto sticky top-0">
              <div className="p-4 overflow-y-auto flex-1">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                  </div>
                  
                <div className="h-full pr-2">
                  {FilterContent}
                </div>
              </div>
      </section>
        
            {/* Medium screens: Absolute positioned overlay */}
            {showFilters && (
              <div className="fixed inset-0 z-50 hidden md:block lg:hidden">
                {/* Backdrop */}
                <div 
                  className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
                  onClick={() => setShowFilters(false)}
                ></div>
                
                {/* Filter panel */}
                <section 
                  className="absolute right-0 top-0 w-[23%] h-full bg-gray-100 overflow-y-auto transform transition-transform duration-300 ease-in-out"
                  style={{
                    animation: 'slideInFromRight 0.3s ease-in-out'
                  }}
                >
                  <div className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFilters(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                    <div>
                      {FilterContent}
                    </div>
                    </div>
                </section>
                    </div>
            )}

            {/* Mobile: Absolute positioned overlay */}
            {isMobileFilterOpen && (
              <div className="fixed inset-0 z-50 lg:hidden">
                {/* Backdrop */}
                <div 
                  className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
                  onClick={() => setIsMobileFilterOpen(false)}
                          ></div>
                          
                {/* Filter panel */}
                <section 
                  className="absolute right-0 top-0 w-[75%] h-full bg-gray-100 overflow-y-auto transform transition-transform duration-300 ease-in-out"
                            style={{ 
                    animation: 'slideInFromRight 0.3s ease-in-out'
                  }}
                >
                  <div className="p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                      <Button 
                        variant="ghost"
                        size="sm" 
                        onClick={() => setIsMobileFilterOpen(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div>
                      {FilterContent}
                  </div>
                </div>
                </section>
              </div>
            )}
      </Card>

      {/* Add Approach Modal */}
      <AddApproachModal
        isOpen={showApproachModal}
        onClose={() => {
          setShowApproachModal(false);
          setSelectedCandidateForApproach(null);
        }}
        candidate={selectedCandidateForApproach}
        fromCandidateDatabase={true}
      />

      {/* Flag Confirm Dialog */}
      <AlertDialog open={!!flagConfirmCandidateId} onOpenChange={(open) => !open && setFlagConfirmCandidateId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                const cand = candidates.find(c => c._id === flagConfirmCandidateId);
                return cand?.flagged ? 'Remove Flag' : 'Flag Candidate';
              })()}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const cand = candidates.find(c => c._id === flagConfirmCandidateId);
                return cand?.flagged
                  ? 'Are you sure you want to remove the flag for this candidate?'
                  : 'Are you sure you want to flag this candidate?';
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFlagConfirmCandidateId(null)}>No</AlertDialogCancel>
            <AlertDialogAction
              className="bg-orange-600 hover:bg-orange-700"
              onClick={async () => {
                if (!flagConfirmCandidateId) return;
                try {
                  const newFlaggedStatus = await toggleCandidateFlag(flagConfirmCandidateId);
                  setCandidates(prevCandidates =>
                    prevCandidates.map(c =>
                      c._id === flagConfirmCandidateId ? { ...c, flagged: newFlaggedStatus } : c
                    )
                  );
                  toast.success(newFlaggedStatus ? 'Candidate flagged' : 'Flag removed');
                } catch (error: any) {
                  toast.error(error.message || 'Failed to toggle flag');
                } finally {
                  setFlagConfirmCandidateId(null);
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CandidateDatabase;

